import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../config/region';
import { formatVnd } from '../../lib/currency';
import DashboardNavBar from './DashboardNavBar';

// ════════════════════════════════════════════════════════════════════════════
// ServiceOrdersManager — OWNER / MANAGER / STAFF panel for the laundry / service
// orders feature. Lists incoming/active orders (with the customer's avatar +
// name so the operator recognises who dropped off), lets staff create a new
// order from the catalog, edit/round lines, tick the status forward, and chat
// with the customer.
//
// All API calls go through getApiBaseUrl() + a Bearer token read from
// localStorage (same pattern as VenueDashboardHomePage / PaymentsPage). The
// venue numeric id is resolved from the slug via GET /venue-dashboard/:slug,
// which also gives us myRole for the role-gated bits.
//
// ALL user-facing text is bilingual EN + VI via the L() helper below, rendered
// by the dashboard's current i18n language (falls back to EN).
// ════════════════════════════════════════════════════════════════════════════

// ─── auth token (matches VenueDashboardHomePage) ─────────────────────────────
function getAuthToken() {
  return (
    localStorage.getItem('venueapp_token') ||
    localStorage.getItem('venue_app_token') ||
    localStorage.getItem('token') ||
    null
  );
}

// ─── bilingual label map ─────────────────────────────────────────────────────
// Every string the operator can see. Render by current language, fall back EN.
const LABELS = {
  title:            { en: 'Service Orders',          vi: 'Đơn dịch vụ' },
  back:             { en: 'Back',                     vi: 'Quay lại' },
  loading:          { en: 'Loading orders…',          vi: 'Đang tải đơn…' },
  newOrder:         { en: 'New order',                vi: 'Đơn mới' },
  noOrders:         { en: 'No orders yet',            vi: 'Chưa có đơn nào' },
  noOrdersSub:      { en: 'New drop-offs will appear here.', vi: 'Đơn mới sẽ hiển thị ở đây.' },
  customer:         { en: 'Customer',                 vi: 'Khách hàng' },
  total:            { en: 'Total',                    vi: 'Tổng' },
  items:           { en: 'items',                     vi: 'món' },
  edit:             { en: 'Edit',                     vi: 'Sửa' },
  chat:             { en: 'Chat',                     vi: 'Nhắn tin' },
  round:            { en: 'Round',                    vi: 'Làm tròn' },
  round1k:          { en: 'Round up to 1,000₫',       vi: 'Làm tròn lên 1.000₫' },
  round5k:          { en: 'Round up to 5,000₫',       vi: 'Làm tròn lên 5.000₫' },
  save:             { en: 'Save',                     vi: 'Lưu' },
  cancel:           { en: 'Cancel',                   vi: 'Huỷ' },
  close:            { en: 'Close',                     vi: 'Đóng' },
  create:          { en: 'Create order',              vi: 'Tạo đơn' },
  customerId:       { en: 'Customer user ID',          vi: 'Mã người dùng khách' },
  customerIdHint:   { en: 'Paste or pick the customer\'s user ID (UUID).', vi: 'Dán hoặc chọn mã người dùng (UUID) của khách.' },
  pickFromCatalog:  { en: 'Add from catalogue',       vi: 'Thêm từ danh mục' },
  weight:           { en: 'Weight (kg)',              vi: 'Cân nặng (kg)' },
  qty:              { en: 'Qty',                       vi: 'SL' },
  unitPrice:        { en: 'Unit price ₫',             vi: 'Đơn giá ₫' },
  lineTotal:        { en: 'Line',                      vi: 'Dòng' },
  notes:            { en: 'Notes',                     vi: 'Ghi chú' },
  remove:           { en: 'Remove',                    vi: 'Xoá' },
  noLines:          { en: 'No lines yet — add from the catalogue.', vi: 'Chưa có dòng nào — thêm từ danh mục.' },
  needCustomer:     { en: 'A customer user ID is required.', vi: 'Cần mã người dùng khách.' },
  needLine:         { en: 'Add at least one line.',     vi: 'Thêm ít nhất một dòng.' },
  statusPanel:      { en: 'Status',                    vi: 'Trạng thái' },
  msgPlaceholder:   { en: 'Type a message…',           vi: 'Nhập tin nhắn…' },
  send:             { en: 'Send',                      vi: 'Gửi' },
  noMessages:       { en: 'No messages yet.',          vi: 'Chưa có tin nhắn.' },
  readyEmailNote:   { en: '”Ready” emails the customer automatically.', vi: '”Sẵn sàng” sẽ tự gửi email cho khách.' },
  filterAll:        { en: 'All',                       vi: 'Tất cả' },
  catalogEmpty:     { en: 'No catalogue items. Add some in the Price List editor.', vi: 'Chưa có món nào. Thêm trong trình sửa Bảng giá.' },
  refresh:          { en: 'Refresh',                   vi: 'Tải lại' },
  accessDenied:     { en: 'You don\'t have access to this venue\'s orders.', vi: 'Bạn không có quyền xem đơn của địa điểm này.' },
  pleaseLogIn:      { en: 'Please log in.',            vi: 'Vui lòng đăng nhập.' },
  // Piece A — override
  overrideTotal:    { en: 'Adjust / override total',   vi: 'Điều chỉnh / ghi đè tổng' },
  overrideHint:     { en: 'Leave blank to use the computed line total.', vi: 'Để trống để dùng tổng tính từ các dòng.' },
  overrideReason:   { en: 'Reason (shown to customer)', vi: 'Lý do (hiển thị cho khách)' },
  clearOverride:    { en: 'Clear override',             vi: 'Xoá ghi đè' },
  computedTotal:    { en: 'Computed total',             vi: 'Tổng tính được' },
  adjustedFrom:     { en: 'Adjusted from',              vi: 'Điều chỉnh từ' },
  // Piece B — here now
  hereNow:          { en: 'Here now',                   vi: 'Đang có mặt' },
  hereNowSub:       { en: 'Customers with open orders in the last 8 hours.', vi: 'Khách có đơn mở trong 8 giờ qua.' },
  noOneHere:        { en: 'No customers here right now.', vi: 'Hiện không có khách nào.' },
  createOrder:      { en: 'Create order',               vi: 'Tạo đơn' },
  // Piece C — pending customer-initiated
  pendingSection:   { en: 'Pending — awaiting your action', vi: 'Chờ xử lý — cần bạn thao tác' },
  pendingSub:       { en: 'Customer started these orders. Add items + price, then confirm.', vi: 'Khách đã tạo đơn này. Thêm hàng + giá, rồi xác nhận.' },
  somethingWentWrong: { en: 'Something went wrong.', vi: 'Đã có lỗi xảy ra.' },
  failedToUpdateStatus: { en: 'Failed to update status', vi: 'Cập nhật trạng thái thất bại' },
  failedToRound:    { en: 'Failed to round',            vi: 'Làm tròn thất bại' },
  failedToCreateOrder: { en: 'Failed to create order',  vi: 'Tạo đơn thất bại' },
  customerFallback: { en: 'Customer',                    vi: 'Khách hàng' },
  failedToSave:     { en: 'Failed to save',              vi: 'Lưu thất bại' },
  itemsSavedOverrideFailed: { en: 'Items saved but override failed', vi: 'Đã lưu món nhưng ghi đè thất bại' },
  roundTotalTo:     { en: 'Round total to',              vi: 'Làm tròn tổng đến' },
  decreaseOverrideTotal: { en: 'Decrease override total by 1,000₫', vi: 'Giảm tổng ghi đè 1.000₫' },
  increaseOverrideTotal: { en: 'Increase override total by 1,000₫', vi: 'Tăng tổng ghi đè 1.000₫' },
  overrideReasonPlaceholder: { en: 'e.g. Round up for convenience', vi: 'VD: làm tròn cho tiện' },
};

// Order statuses → bilingual chip + colour. Mirrors the backend lifecycle.
const STATUS_META = {
  draft:                          { en: 'Draft',           vi: 'Nháp',           bg: '#f3f4f6', fg: '#6b7280' },
  awaiting_customer_confirmation: { en: 'Awaiting confirm', vi: 'Chờ xác nhận',  bg: '#fef9c3', fg: '#854d0e' },
  confirmed:                      { en: 'Accepted',        vi: 'Đã nhận',        bg: '#dcfce7', fg: '#166534' },
  in_progress:                    { en: 'Washing',         vi: 'Đang giặt',      bg: '#dbeafe', fg: '#1e40af' },
  ready:                          { en: 'Ready to collect', vi: 'Sẵn sàng lấy',  bg: '#ede9fe', fg: '#6d28d9' },
  collected:                      { en: 'Collected',       vi: 'Đã lấy',         bg: '#e5e7eb', fg: '#374151' },
  cancelled:                      { en: 'Cancelled',       vi: 'Đã huỷ',         bg: '#fee2e2', fg: '#991b1b' },
};

// The "tick forward" steps the operator advances through (POST .../status).
const ADVANCE_STEPS = [
  { status: 'in_progress', en: 'Washing',          vi: 'Đang giặt' },
  { status: 'ready',       en: 'Ready to collect',  vi: 'Sẵn sàng lấy' },
  { status: 'collected',   en: 'Collected',         vi: 'Đã lấy' },
];

const STATUS_FILTERS = [
  'awaiting_customer_confirmation',
  'confirmed',
  'in_progress',
  'ready',
  'collected',
  'cancelled',
];

export default function ServiceOrdersManager() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  // current language ('en' | 'vi' | …) → bilingual lookup, fall back to EN.
  const lang = (i18n.language || 'en').split('-')[0];
  const L = useCallback(
    (key) => {
      const m = LABELS[key];
      if (!m) return key;
      return m[lang] || m.en;
    },
    [lang],
  );
  // Bilingual status / step label for an arbitrary {en, vi} map.
  const LX = useCallback((m) => (m ? m[lang] || m.en : ''), [lang]);

  const base = getApiBaseUrl();
  const token = getAuthToken();

  const [phase, setPhase] = useState('loading'); // loading | ready | error
  const [errType, setErrType] = useState(null);   // 401 | 403 | generic
  const [venue, setVenue] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [catalog, setCatalog] = useState([]); // flat list of catalogue items

  // Piece B — "Here now" presence panel
  const [checkedIn, setCheckedIn] = useState([]); // customers with open draft orders (last 8h)
  const [checkedInLoading, setCheckedInLoading] = useState(false);

  const [creating, setCreating] = useState(false); // new-order modal open
  const [createPrefillCustomer, setCreatePrefillCustomer] = useState(null); // { user_id, name, photo_url }
  const [editOrder, setEditOrder] = useState(null); // order being edited
  const [chatOrder, setChatOrder] = useState(null); // order whose chat is open

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  // ── resolve venue (id + role) from slug ─────────────────────────────────────
  const loadVenue = useCallback(async () => {
    if (!token) { setPhase('error'); setErrType(401); return null; }
    try {
      const res = await fetch(`${base}/venue-dashboard/${encodeURIComponent(slug)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) { setPhase('error'); setErrType(401); return null; }
      if (res.status === 403) { setPhase('error'); setErrType(403); return null; }
      const data = await res.json();
      if (!data.success || !data.venue) { setPhase('error'); setErrType('generic'); return null; }
      if (!data.myRole) { setPhase('error'); setErrType(403); return null; }
      setVenue(data.venue);
      setMyRole(data.myRole);
      return data.venue;
    } catch (e) {
      console.error('[ServiceOrdersManager] venue load', e);
      setPhase('error'); setErrType('generic');
      return null;
    }
  }, [base, slug, token]);

  // ── load the venue's orders (optionally status-filtered) ────────────────────
  const loadOrders = useCallback(async (venueId, status) => {
    try {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      const res = await fetch(`${base}/me/venues/${venueId}/service-orders${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setOrders(data.orders || []);
    } catch (e) {
      console.warn('[ServiceOrdersManager] orders load', e);
      // Non-fatal: leave orders as [] — the empty-state render handles it.
    }
  }, [base, token]);

  // ── load the catalogue (public endpoint, by slug) ───────────────────────────
  const loadCatalog = useCallback(async () => {
    try {
      const res = await fetch(`${base}/venues/${encodeURIComponent(slug)}/services`);
      const data = await res.json();
      if (data.success && data.data?.categories) {
        const flat = [];
        for (const cat of data.data.categories) {
          for (const it of cat.items || []) flat.push({ ...it, category: cat.name });
        }
        setCatalog(flat);
      }
    } catch (e) {
      console.warn('[ServiceOrdersManager] catalog load', e);
    }
  }, [base, slug]);

  // ── Piece B: load who is checked in (open draft orders in last 8h) ───────────
  const loadCheckedIn = useCallback(async (venueId) => {
    setCheckedInLoading(true);
    try {
      const res = await fetch(`${base}/me/venues/${venueId}/checked-in`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setCheckedIn(data.customers || []);
    } catch (e) {
      console.warn('[ServiceOrdersManager] checked-in load', e);
    } finally {
      setCheckedInLoading(false);
    }
  }, [base, token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await loadVenue();
        if (!v || cancelled) return;
        // Run orders + catalog + checked-in in parallel; all individually error-safe.
        await Promise.all([loadOrders(v.id, ''), loadCatalog(), loadCheckedIn(v.id)]);
      } catch (e) {
        // loadVenue already sets phase/errType on its own errors.
        // This catch guards against any unexpected throw so phase never sticks on 'loading'.
        console.error('[ServiceOrdersManager] init error', e);
        if (!cancelled) { setPhase('error'); setErrType('generic'); }
        return;
      }
      if (!cancelled) setPhase('ready');
    })();
    return () => { cancelled = true; };
  }, [loadVenue, loadOrders, loadCatalog, loadCheckedIn]);

  // refetch orders when the filter changes
  useEffect(() => {
    if (phase === 'ready' && venue) loadOrders(venue.id, statusFilter);
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(() => {
    if (venue) {
      loadOrders(venue.id, statusFilter);
      loadCheckedIn(venue.id);
    }
  }, [venue, statusFilter, loadOrders, loadCheckedIn]);

  // ── advance an order's status ───────────────────────────────────────────────
  const advanceStatus = useCallback(async (order, status) => {
    const res = await fetch(`${base}/me/service-orders/${order.id}/status`, {
      method: 'POST', headers: authHeaders, body: JSON.stringify({ status }),
    });
    const data = await res.json();
    if (data.success) {
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, ...data.order } : o)));
    } else {
      alert(data.message || L('failedToUpdateStatus'));
    }
  }, [base, authHeaders, L]);

  // ── round an order's total ──────────────────────────────────────────────────
  const roundOrder = useCallback(async (order, roundTo) => {
    const res = await fetch(`${base}/me/service-orders/${order.id}`, {
      method: 'PATCH', headers: authHeaders, body: JSON.stringify({ round_to: roundTo }),
    });
    const data = await res.json();
    if (data.success) {
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, ...data.order, items: data.items || o.items } : o)));
    } else {
      alert(data.message || L('failedToRound'));
    }
  }, [base, authHeaders, L]);

  // ════════════════════════════════════════════════════════════════════════
  // render
  // ════════════════════════════════════════════════════════════════════════
  // Piece C — separate pending (draft) customer-initiated orders from staff-built ones.
  // 🔴 These useMemo hooks MUST live ABOVE the early returns below. Placing them
  // after the phase==='loading'/'error' returns broke the Rules of Hooks
  // ("Rendered more hooks than during the previous render") and white-screened
  // the whole Service Orders page the instant phase went loading -> ready.
  const pendingOrders = useMemo(
    () => orders.filter((o) => o.status === 'draft' && !o.created_by_user_id_is_staff),
    [orders],
  );
  const activeOrders = useMemo(
    () => orders.filter((o) => o.status !== 'draft'),
    [orders],
  );

  if (phase === 'loading') {
    return (
      <div style={S.screen}>
        <Header title={L('title')} onBack={() => navigate(`/venue-dashboard/${slug}`)} />
        <div style={S.centerBox}>
          <Spinner />
          <p style={{ color: '#6b7280', fontSize: 14 }}>{L('loading')}</p>
        </div>
        <DashboardNavBar />
      </div>
    );
  }

  if (phase === 'error') {
    const msg = errType === 401 ? L('pleaseLogIn') : errType === 403 ? L('accessDenied') : L('somethingWentWrong');
    return (
      <div style={S.screen}>
        <Header title={L('title')} onBack={() => navigate(`/venue-dashboard/${slug}`)} />
        <div style={S.centerBox}>
          <div style={{ fontSize: 44 }}>{errType === 403 ? '🚫' : errType === 401 ? '🔒' : '⚠️'}</div>
          <p style={{ color: '#374151', fontSize: 15, textAlign: 'center', maxWidth: 280 }}>{msg}</p>
          {errType === 401 && (
            <button style={S.primaryBtn} onClick={() => navigate('/login')}>{L('pleaseLogIn')}</button>
          )}
        </div>
        <DashboardNavBar />
      </div>
    );
  }

  // When no filter, show draft orders as a separate "pending" section above the main list.
  const showDraftSection = statusFilter === '' && pendingOrders.length > 0;
  const mainOrders = statusFilter === '' ? activeOrders : orders;

  return (
    <div style={S.screen}>
      <Header
        title={venue?.name || L('title')}
        onBack={() => navigate(`/venue-dashboard/${slug}`)}
        right={
          <button style={S.headerBtn} onClick={() => { setCreatePrefillCustomer(null); setCreating(true); }}>+ {L('newOrder')}</button>
        }
      />

      <div style={S.body}>
        {/* ── Piece B: "Here now" presence panel ─────────────────────────── */}
        <HereNowPanel
          customers={checkedIn}
          loading={checkedInLoading}
          lang={lang}
          L={L}
          onCreateOrder={(customer) => {
            setCreatePrefillCustomer(customer);
            setCreating(true);
          }}
        />

        {/* ── Piece C: Pending customer-initiated orders ───────────────────── */}
        {showDraftSection && (
          <div style={S.sectionBlock}>
            <div style={S.sectionHeader}>
              <span style={{ ...S.sectionDot, background: '#f97316' }} />
              <span style={S.sectionTitle}>{L('pendingSection')}</span>
              <span style={S.sectionCount}>{pendingOrders.length}</span>
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8 }}>{L('pendingSub')}</div>
            {pendingOrders.map((o) => (
              <OrderCard
                key={o.id}
                order={o}
                lang={lang}
                L={L}
                LX={LX}
                highlight
                onEdit={() => setEditOrder(o)}
                onChat={() => setChatOrder(o)}
                onAdvance={(status) => advanceStatus(o, status)}
                onRound={(roundTo) => roundOrder(o, roundTo)}
              />
            ))}
          </div>
        )}

        {/* status filter chips */}
        <div style={S.filterRow}>
          <FilterChip active={statusFilter === ''} onClick={() => setStatusFilter('')} label={L('filterAll')} />
          {STATUS_FILTERS.map((s) => (
            <FilterChip
              key={s}
              active={statusFilter === s}
              onClick={() => setStatusFilter(s)}
              label={LX(STATUS_META[s])}
            />
          ))}
          <button style={S.refreshBtn} onClick={refresh} title={L('refresh')}>↻</button>
        </div>

        {mainOrders.length === 0 && !showDraftSection ? (
          <div style={S.emptyBox}>
            <div style={{ fontSize: 48 }}>🧺</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#111827', marginTop: 8 }}>{L('noOrders')}</div>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{L('noOrdersSub')}</div>
          </div>
        ) : (
          mainOrders.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              lang={lang}
              L={L}
              LX={LX}
              onEdit={() => setEditOrder(o)}
              onChat={() => setChatOrder(o)}
              onAdvance={(status) => advanceStatus(o, status)}
              onRound={(roundTo) => roundOrder(o, roundTo)}
            />
          ))
        )}
        <div style={{ height: 48 }} />
      </div>

      {creating && (
        <NewOrderModal
          base={base}
          venueId={venue.id}
          authHeaders={authHeaders}
          catalog={catalog}
          lang={lang}
          L={L}
          prefillCustomer={createPrefillCustomer}
          onClose={() => { setCreating(false); setCreatePrefillCustomer(null); }}
          onCreated={() => { setCreating(false); setCreatePrefillCustomer(null); refresh(); }}
        />
      )}

      {editOrder && (
        <EditOrderModal
          base={base}
          authHeaders={authHeaders}
          order={editOrder}
          catalog={catalog}
          lang={lang}
          L={L}
          onClose={() => setEditOrder(null)}
          onSaved={() => { setEditOrder(null); refresh(); }}
        />
      )}

      {chatOrder && (
        <ChatModal
          base={base}
          token={token}
          authHeaders={authHeaders}
          order={chatOrder}
          L={L}
          onClose={() => setChatOrder(null)}
        />
      )}

      <DashboardNavBar />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Order card
// ════════════════════════════════════════════════════════════════════════════
function OrderCard({ order, L, LX, onEdit, onChat, onAdvance, onRound, highlight }) {
  const meta = STATUS_META[order.status] || STATUS_META.draft;
  const itemCount = (order.items || []).length;
  const computedTotal = Number(order.total_vnd || 0);
  // Piece A: use override when set; show "adjusted from ₫X" annotation.
  const hasOverride = order.total_override_vnd != null;
  const total = hasOverride ? Number(order.total_override_vnd) : computedTotal;
  const isTerminal = order.status === 'collected' || order.status === 'cancelled';

  return (
    <div style={{ ...S.card, ...(highlight ? { border: '2px solid #f97316' } : {}) }}>
      {/* header row: customer avatar + name + status chip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar src={order.customer_avatar} name={order.customer_name} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {order.customer_name || order.customer_email || L('customer')}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>
            {order.order_ref} · {itemCount} {L('items')}
          </div>
        </div>
        <span style={{ ...S.statusChip, background: meta.bg, color: meta.fg }}>{LX(meta)}</span>
      </div>

      {/* line items */}
      {itemCount > 0 && (
        <div style={{ marginTop: 10, borderTop: '1px solid #f3f4f6', paddingTop: 8 }}>
          {(order.items || []).map((it) => (
            <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#374151', padding: '2px 0' }}>
              <span>
                {it.name_snapshot}
                {it.unit === 'per_kg' && it.weight_kg != null ? ` · ${Number(it.weight_kg)}kg` : it.qty > 1 ? ` ×${it.qty}` : ''}
              </span>
              <span style={{ fontWeight: 600 }}>{formatVnd(Number(it.line_total_vnd || 0))}</span>
            </div>
          ))}
        </div>
      )}

      {/* total — Piece A: show override annotation when active */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 8, borderTop: '1px solid #f3f4f6' }}>
        <div>
          <span style={{ fontSize: 13, color: '#6b7280' }}>{L('total')}</span>
          {hasOverride && computedTotal !== total && (
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
              {L('adjustedFrom')} {formatVnd(computedTotal)}
              {order.adjustment_reason ? ` · ${order.adjustment_reason}` : ''}
            </div>
          )}
        </div>
        <span style={{ fontSize: 18, fontWeight: 800, color: hasOverride ? '#b45309' : '#111827' }}>
          {formatVnd(total)}
        </span>
      </div>

      {/* status tick panel */}
      {!isTerminal && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            {L('statusPanel')}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ADVANCE_STEPS.map((step) => {
              const isCurrent = order.status === step.status;
              const done = stepIsDone(order.status, step.status);
              return (
                <button
                  key={step.status}
                  onClick={() => onAdvance(step.status)}
                  disabled={isCurrent}
                  style={{
                    ...S.stepBtn,
                    background: isCurrent ? '#f97316' : done ? '#dcfce7' : '#fff',
                    color: isCurrent ? '#fff' : done ? '#166534' : '#374151',
                    borderColor: isCurrent ? '#f97316' : done ? '#86efac' : '#e5e7eb',
                  }}
                >
                  {done && !isCurrent ? '✓ ' : ''}{LX(step)}
                </button>
              );
            })}
            <button
              onClick={() => onAdvance('cancelled')}
              style={{ ...S.stepBtn, background: '#fff', color: '#991b1b', borderColor: '#fecaca' }}
            >
              ✕ {LX(STATUS_META.cancelled)}
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>📧 {L('readyEmailNote')}</div>
        </div>
      )}

      {/* actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button style={S.actionBtn} onClick={onEdit}>✏️ {L('edit')}</button>
        <button style={S.actionBtn} onClick={onChat}>💬 {L('chat')}</button>
        <div style={{ position: 'relative' }}>
          <RoundMenu L={L} onRound={onRound} />
        </div>
      </div>
    </div>
  );
}

// returns true if `target` status is at/before the order's current status in the flow
function stepIsDone(currentStatus, target) {
  const flow = ['confirmed', 'in_progress', 'ready', 'collected'];
  const ci = flow.indexOf(currentStatus);
  const ti = flow.indexOf(target);
  if (ci < 0 || ti < 0) return false;
  return ti < ci;
}

function RoundMenu({ L, onRound }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button style={S.actionBtn} onClick={() => setOpen((v) => !v)}>🎯 {L('round')}</button>
      {open && (
        <div style={S.roundPop}>
          <button style={S.roundOpt} onClick={() => { onRound(1000); setOpen(false); }}>{L('round1k')}</button>
          <button style={S.roundOpt} onClick={() => { onRound(5000); setOpen(false); }}>{L('round5k')}</button>
        </div>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// New-order modal
// ════════════════════════════════════════════════════════════════════════════
// prefillCustomer = { user_id, name, photo_url } from the "Here now" panel
function NewOrderModal({ base, venueId, authHeaders, catalog, lang, L, onClose, onCreated, prefillCustomer }) {
  const [customerUserId, setCustomerUserId] = useState(() => prefillCustomer?.user_id || '');
  const [lines, setLines] = useState([]); // {service_item_id, name, name_i18n, unit, qty, weight_kg, unit_price_vnd, modifiers}
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const total = useMemo(() => lines.reduce((s, l) => s + lineTotal(l), 0), [lines]);

  const addFromCatalog = (item) => {
    setLines((prev) => [
      ...prev,
      {
        service_item_id: item.id,
        name: item.name_en,
        name_i18n: item.name_i18n || {},
        unit: item.unit,
        qty: 1,
        weight_kg: item.unit === 'per_kg' ? 1 : null,
        unit_price_vnd: item.price_vnd != null ? Number(item.price_vnd) : 0,
        modifiers: [],
      },
    ]);
  };

  const submit = async () => {
    setErr('');
    if (!customerUserId.trim()) { setErr(L('needCustomer')); return; }
    if (lines.length === 0) { setErr(L('needLine')); return; }
    setBusy(true);
    try {
      const res = await fetch(`${base}/me/venues/${venueId}/service-orders`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ customer_user_id: customerUserId.trim(), items: lines, notes: notes || null }),
      });
      const data = await res.json();
      if (data.success) onCreated();
      else setErr(data.message || L('failedToCreateOrder'));
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={L('newOrder')} onClose={onClose} L={L}>
      {/* customer — prefilled from "Here now" panel or manual UUID entry */}
      {prefillCustomer ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#fff7ed', borderRadius: 10, marginBottom: 12 }}>
          <Avatar src={prefillCustomer.photo_url} name={prefillCustomer.name} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{prefillCustomer.name || L('customerFallback')}</div>
            <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis' }}>{prefillCustomer.user_id}</div>
          </div>
          <button style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 18, cursor: 'pointer' }} onClick={() => setCustomerUserId('')}>✕</button>
        </div>
      ) : (
        <>
          <label style={S.fieldLabel}>{L('customerId')}</label>
          <input
            style={S.input}
            value={customerUserId}
            onChange={(e) => setCustomerUserId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
          />
          <div style={S.hint}>{L('customerIdHint')}</div>
        </>
      )}

      <LineEditor lines={lines} setLines={setLines} lang={lang} L={L} />

      {/* catalogue picker */}
      <CatalogPicker catalog={catalog} lang={lang} L={L} onPick={addFromCatalog} />

      <label style={{ ...S.fieldLabel, marginTop: 12 }}>{L('notes')}</label>
      <textarea style={{ ...S.input, minHeight: 56 }} value={notes} onChange={(e) => setNotes(e.target.value)} />

      <TotalBar total={total} L={L} />

      {err && <div style={S.errorBox}>{err}</div>}
      <div style={S.modalActions}>
        <button style={S.secondaryBtn} onClick={onClose} disabled={busy}>{L('cancel')}</button>
        <button style={S.primaryBtn} onClick={submit} disabled={busy}>{busy ? '…' : L('create')}</button>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Edit-order modal (replace lines → PATCH items[] + Piece A: price override)
// ════════════════════════════════════════════════════════════════════════════
function EditOrderModal({ base, authHeaders, order, catalog, lang, L, onClose, onSaved }) {
  const [lines, setLines] = useState(() =>
    (order.items || []).map((it) => ({
      service_item_id: it.service_item_id,
      name: it.name_snapshot,
      name_i18n: it.name_i18n || {},
      unit: it.unit,
      qty: it.qty != null ? Number(it.qty) : 1,
      weight_kg: it.weight_kg != null ? Number(it.weight_kg) : null,
      unit_price_vnd: it.unit_price_vnd != null ? Number(it.unit_price_vnd) : 0,
      modifiers: it.modifiers || [],
    })),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Piece A — total override state
  // overrideInput: empty string = no override; number string = manual override
  const [overrideInput, setOverrideInput] = useState(
    () => order.total_override_vnd != null ? String(Number(order.total_override_vnd)) : '',
  );
  const [overrideReason, setOverrideReason] = useState(() => order.adjustment_reason || '');

  const computedTotal = useMemo(() => lines.reduce((s, l) => s + lineTotal(l), 0), [lines]);
  const hasOverride = overrideInput.trim() !== '';
  const effectiveTotal = hasOverride
    ? (Number(overrideInput) || computedTotal)
    : computedTotal;

  const addFromCatalog = (item) => {
    setLines((prev) => [
      ...prev,
      {
        service_item_id: item.id,
        name: item.name_en,
        name_i18n: item.name_i18n || {},
        unit: item.unit,
        qty: 1,
        weight_kg: item.unit === 'per_kg' ? 1 : null,
        unit_price_vnd: item.price_vnd != null ? Number(item.price_vnd) : 0,
        modifiers: [],
      },
    ]);
  };

  // Step 1: save the lines (PATCH items[]). Step 2: if an override is set,
  // PATCH total_override_vnd separately (because items[] PATCH clears the override
  // server-side, so we re-apply it right after).
  const submit = async () => {
    setErr('');
    if (lines.length === 0) { setErr(L('needLine')); return; }
    setBusy(true);
    try {
      // Save items first.
      const res = await fetch(`${base}/me/service-orders/${order.id}`, {
        method: 'PATCH', headers: authHeaders, body: JSON.stringify({ items: lines }),
      });
      const data = await res.json();
      if (!data.success) { setErr(data.message || L('failedToSave')); return; }

      // Then apply/clear the override if the field was touched.
      const overrideVal = overrideInput.trim() !== '' ? Number(overrideInput) : null;
      const existingOverride = order.total_override_vnd != null ? Number(order.total_override_vnd) : null;
      const overrideTouched = overrideVal !== existingOverride || overrideReason !== (order.adjustment_reason || '');
      if (overrideTouched) {
        const oRes = await fetch(`${base}/me/service-orders/${order.id}`, {
          method: 'PATCH', headers: authHeaders,
          body: JSON.stringify({ total_override_vnd: overrideVal, adjustment_reason: overrideReason || null }),
        });
        const oData = await oRes.json();
        if (!oData.success) { setErr(oData.message || L('itemsSavedOverrideFailed')); return; }
      }

      onSaved();
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`${L('edit')} · ${order.order_ref}`} onClose={onClose} L={L}>
      <LineEditor lines={lines} setLines={setLines} lang={lang} L={L} />
      <CatalogPicker catalog={catalog} lang={lang} L={L} onPick={addFromCatalog} />

      {/* Piece A — price override section */}
      <div style={{ marginTop: 14, padding: '12px 14px', background: '#fffbeb', borderRadius: 10, border: '1px solid #fde68a' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>🎯 {L('overrideTotal')}</span>
          {hasOverride && (
            <button
              style={{ background: 'none', border: 'none', fontSize: 11, color: '#9ca3af', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => { setOverrideInput(''); setOverrideReason(''); }}
            >
              {L('clearOverride')}
            </button>
          )}
        </div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8 }}>
          {L('computedTotal')}: <strong style={{ color: '#111827' }}>{formatVnd(computedTotal)}</strong>
        </div>
        {/* Override total stepper — steps of 1,000 ₫; label reads "Round total to" */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>{L('roundTotalTo')}</span>
          <div style={S.stepperWrap}>
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                const cur = overrideInput.trim() !== '' ? Number(overrideInput) : computedTotal;
                const next = Math.max(0, cur - 1000);
                setOverrideInput(String(next));
              }}
              style={{ ...S.stepperBtn, borderTopLeftRadius: 8, borderBottomLeftRadius: 8, borderRight: 'none' }}
              aria-label={L('decreaseOverrideTotal')}
            >−</button>
            <input
              type="number"
              step="1000"
              min="0"
              placeholder={String(computedTotal)}
              value={overrideInput}
              onChange={(e) => setOverrideInput(e.target.value)}
              style={{ ...S.stepperInput, flex: 1, minWidth: 0 }}
            />
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                const cur = overrideInput.trim() !== '' ? Number(overrideInput) : computedTotal;
                const next = cur + 1000;
                setOverrideInput(String(next));
              }}
              style={{ ...S.stepperBtn, borderTopRightRadius: 8, borderBottomRightRadius: 8, borderLeft: 'none' }}
              aria-label={L('increaseOverrideTotal')}
            >+</button>
          </div>
        </div>
        <div style={S.hint}>{L('overrideHint')}</div>
        <label style={{ ...S.fieldLabel, marginTop: 8 }}>{L('overrideReason')}</label>
        <input
          type="text"
          placeholder={L('overrideReasonPlaceholder')}
          value={overrideReason}
          onChange={(e) => setOverrideReason(e.target.value)}
          style={{ ...S.input, marginBottom: 0 }}
        />
      </div>

      <TotalBar total={effectiveTotal} L={L} overrideActive={hasOverride} computedTotal={computedTotal} />
      {err && <div style={S.errorBox}>{err}</div>}
      <div style={S.modalActions}>
        <button style={S.secondaryBtn} onClick={onClose} disabled={busy}>{L('cancel')}</button>
        <button style={S.primaryBtn} onClick={submit} disabled={busy}>{busy ? '…' : L('save')}</button>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Shared: line editor (qty / weight / price per line)
// ════════════════════════════════════════════════════════════════════════════
function LineEditor({ lines, setLines, lang, L }) {
  const update = (idx, patch) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const remove = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  if (lines.length === 0) {
    return <div style={{ ...S.hint, padding: '10px 0' }}>{L('noLines')}</div>;
  }
  return (
    <div style={{ marginTop: 10 }}>
      {lines.map((l, idx) => {
        const localName = (l.name_i18n && l.name_i18n[lang]) || l.name;
        return (
          <div key={idx} style={S.lineRow}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{localName}</span>
              <button style={S.removeBtn} onClick={() => remove(idx)}>{L('remove')}</button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              {l.unit === 'per_kg' ? (
                <StepperField
                  label={L('weight')} value={l.weight_kg ?? 0}
                  step={0.1} decimals={1} min={0}
                  onChange={(v) => update(idx, { weight_kg: v })}
                />
              ) : (
                <StepperField
                  label={L('qty')} value={l.qty ?? 1}
                  step={1} decimals={0} min={0}
                  onChange={(v) => update(idx, { qty: v })}
                />
              )}
              <StepperField
                label={L('unitPrice')} value={l.unit_price_vnd ?? 0}
                step={1000} decimals={0} min={0}
                onChange={(v) => update(idx, { unit_price_vnd: v })}
              />
              <div style={{ marginLeft: 'auto', alignSelf: 'flex-end', textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{L('lineTotal')}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{formatVnd(lineTotal(l))}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CatalogPicker({ catalog, lang, L, onPick }) {
  if (!catalog || catalog.length === 0) {
    return <div style={{ ...S.hint, marginTop: 10 }}>{L('catalogEmpty')}</div>;
  }
  return (
    <div style={{ marginTop: 12 }}>
      <div style={S.fieldLabel}>{L('pickFromCatalog')}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {catalog.map((item) => {
          const name = (item.name_i18n && item.name_i18n[lang]) || item.name_en;
          return (
            <button key={item.id} style={S.catChip} onClick={() => onPick(item)}>
              {item.icon ? `${item.icon} ` : ''}{name}
              {item.price_vnd != null ? ` · ${formatVnd(Number(item.price_vnd))}${item.unit === 'per_kg' ? '/kg' : ''}` : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TotalBar({ total, L, overrideActive, computedTotal }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, padding: '10px 12px', background: overrideActive ? '#fef3c7' : '#fff7ed', borderRadius: 10, border: overrideActive ? '1.5px solid #fde68a' : 'none' }}>
      <div>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#9a3412' }}>{L('total')}</span>
        {overrideActive && computedTotal != null && total !== computedTotal && (
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{L('adjustedFrom')} {formatVnd(computedTotal)}</div>
        )}
      </div>
      <span style={{ fontSize: 20, fontWeight: 800, color: overrideActive ? '#b45309' : '#9a3412' }}>{formatVnd(total)}</span>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Chat modal — uses the order's thread (resolved via GET /me/threads)
// ════════════════════════════════════════════════════════════════════════════
function ChatModal({ base, token, authHeaders, order, L, onClose }) {
  const [threadId, setThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Resolve the thread for this order, then load its messages.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const tRes = await fetch(`${base}/me/threads`, { headers: { Authorization: `Bearer ${token}` } });
        const tData = await tRes.json();
        const thread = (tData.threads || []).find(
          (t) => t.service_order_id === order.id || t.order_ref === order.order_ref,
        );
        if (!thread) { if (!cancelled) { setThreadId(null); setLoading(false); } return; }
        if (cancelled) return;
        setThreadId(thread.id);
        const mRes = await fetch(`${base}/me/threads/${thread.id}/messages`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const mData = await mRes.json();
        if (!cancelled && mData.success) setMessages(mData.messages || []);
      } catch (e) {
        console.warn('[ChatModal] load', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [base, token, order.id, order.order_ref]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const body = draft.trim();
    if (!body || !threadId) return;
    setSending(true);
    try {
      const res = await fetch(`${base}/me/threads/${threadId}/messages`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ body }),
      });
      const data = await res.json();
      if (data.success) {
        setMessages((prev) => [...prev, data.message]);
        setDraft('');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal title={`💬 ${order.customer_name || L('chat')}`} onClose={onClose} L={L}>
      <div ref={scrollRef} style={S.chatScroll}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}><Spinner size={28} /></div>
        ) : messages.length === 0 ? (
          <div style={{ ...S.hint, textAlign: 'center', padding: 24 }}>{L('noMessages')}</div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_role && m.sender_role !== 'customer' && m.sender_role !== 'system';
            const system = m.sender_role === 'system';
            if (system) {
              return (
                <div key={m.id} style={S.sysMsg}>{m.body}</div>
              );
            }
            return (
              <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', margin: '4px 0' }}>
                <div style={{ ...S.bubble, background: mine ? '#f97316' : '#f3f4f6', color: mine ? '#fff' : '#111827' }}>
                  {m.body}
                </div>
              </div>
            );
          })
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <input
          style={{ ...S.input, marginBottom: 0, flex: 1 }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder={L('msgPlaceholder')}
          disabled={!threadId}
        />
        <button style={S.primaryBtn} onClick={send} disabled={sending || !threadId || !draft.trim()}>{L('send')}</button>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Piece B — "Here now" presence panel
// Customers who have an open draft/active order in the last 8 hours.
// Shows their profile photo + name + a "Create order" button.
// ════════════════════════════════════════════════════════════════════════════
function HereNowPanel({ customers, loading, lang, L, onCreateOrder }) {
  const [collapsed, setCollapsed] = useState(false);
  // Hide entirely when no customers and not loading (avoids a stale empty panel
  // on every page load before the API responds).
  if (!loading && customers.length === 0) return null;

  return (
    <div style={S.hereNowPanel}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        onClick={() => setCollapsed((v) => !v)}
      >
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', flexShrink: 0, boxShadow: '0 0 0 3px rgba(34,197,94,0.25)' }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: '#15803d', flex: 1 }}>
          {L('hereNow')}
          {customers.length > 0 && ` (${customers.length})`}
        </span>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>{collapsed ? '▸' : '▾'}</span>
      </div>
      {!collapsed && (
        <>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3, marginBottom: 8 }}>{L('hereNowSub')}</div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}><Spinner size={20} color="#22c55e" /></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {customers.map((c) => {
                const statusMeta = STATUS_META[c.status] || STATUS_META.draft;
                return (
                  <div key={c.order_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb' }}>
                    <Avatar src={c.photo_url} name={c.name} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.name || c.email || L('customerFallback')}
                      </div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>
                        {c.order_ref}
                        {' · '}
                        <span style={{ color: statusMeta.fg, fontWeight: 600 }}>
                          {statusMeta[lang] || statusMeta.en}
                        </span>
                      </div>
                    </div>
                    {c.status === 'draft' && (
                      <button
                        style={{ ...S.actionBtn, fontSize: 12, padding: '6px 10px', background: '#fff7ed', borderColor: '#fed7aa', color: '#9a3412' }}
                        onClick={() => onCreateOrder({ user_id: c.user_id, name: c.name, photo_url: c.photo_url })}
                      >
                        ＋ {L('createOrder')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// small shared UI bits
// ════════════════════════════════════════════════════════════════════════════
function Header({ title, onBack, right }) {
  return (
    <div style={S.header}>
      <button style={S.backBtn} onClick={onBack}>←</button>
      <span style={S.headerTitle} title={title}>{title}</span>
      <div style={{ minWidth: 40, display: 'flex', justifyContent: 'flex-end' }}>{right || <div style={{ width: 40 }} />}</div>
    </div>
  );
}

function Modal({ title, onClose, L, children }) {
  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalSheet} onClick={(e) => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>{title}</span>
          <button style={S.modalClose} onClick={onClose}>✕</button>
        </div>
        <div style={{ overflowY: 'auto', padding: 16 }}>{children}</div>
      </div>
    </div>
  );
}

function Avatar({ src, name }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  if (src) {
    return <img src={src} alt={name || ''} style={S.avatar} />;
  }
  return (
    <div style={{ ...S.avatar, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f97316', color: '#fff', fontWeight: 700 }}>
      {initial}
    </div>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        ...S.filterChip,
        background: active ? '#f97316' : '#fff',
        color: active ? '#fff' : '#6b7280',
        borderColor: active ? '#f97316' : '#e5e7eb',
      }}
    >
      {label}
    </button>
  );
}

function NumField({ label, value, step, onChange }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 11, color: '#9ca3af' }}>{label}</span>
      <input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        style={S.numInput}
      />
    </label>
  );
}

// ─── StepperField — tap-friendly -/+ flanking a number input ─────────────────
// step: numeric step size (1 for qty, 0.1 for kg, 1000 for VND override).
// decimals: display precision (0 for integers/VND, 1 for kg).
// min: minimum value (default 0).
function StepperField({ label, value, step = 1, decimals = 0, min = 0, onChange }) {
  const numStep = Number(step);
  const numMin = Number(min);

  const dec = (e) => {
    e.preventDefault();
    const next = Math.max(numMin, parseFloat((Number(value) - numStep).toFixed(decimals)));
    onChange(next);
  };
  const inc = (e) => {
    e.preventDefault();
    const next = parseFloat((Number(value) + numStep).toFixed(decimals));
    onChange(next);
  };

  const displayVal = decimals > 0
    ? Number(value).toFixed(decimals)
    : String(Number(value));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {label && <span style={{ fontSize: 11, color: '#9ca3af' }}>{label}</span>}
      <div style={S.stepperWrap}>
        <button
          type="button"
          onMouseDown={dec}
          style={{
            ...S.stepperBtn,
            borderTopLeftRadius: 8, borderBottomLeftRadius: 8,
            borderRight: 'none',
          }}
          aria-label={`Decrease ${label}`}
        >−</button>
        <input
          type="number"
          step={step}
          min={min}
          value={displayVal}
          onChange={(e) => {
            const v = e.target.value === '' ? numMin : Number(e.target.value);
            onChange(Math.max(numMin, parseFloat(v.toFixed(decimals))));
          }}
          style={S.stepperInput}
        />
        <button
          type="button"
          onMouseDown={inc}
          style={{
            ...S.stepperBtn,
            borderTopRightRadius: 8, borderBottomRightRadius: 8,
            borderLeft: 'none',
          }}
          aria-label={`Increase ${label}`}
        >+</button>
      </div>
    </div>
  );
}

function Spinner({ size = 40, color = '#f97316' }) {
  return (
    <>
      <div style={{ width: size, height: size, border: '4px solid rgba(0,0,0,0.08)', borderTop: `4px solid ${color}`, borderRadius: '50%', animation: 'hl-spin 0.9s linear infinite', margin: '0 auto' }} />
      <style>{`@keyframes hl-spin{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
    </>
  );
}

// ── compute a single line total (mirrors backend computeLineTotal) ───────────
function lineTotal(l) {
  const unitPrice = Number(l.unit_price_vnd || 0);
  const base =
    l.unit === 'per_kg' && l.weight_kg != null
      ? unitPrice * Number(l.weight_kg)
      : unitPrice * Number(l.qty || 1);
  let modSum = 0;
  if (Array.isArray(l.modifiers)) for (const m of l.modifiers) modSum += Number(m?.price_delta_vnd || 0);
  return Math.round(base + modSum);
}

// ════════════════════════════════════════════════════════════════════════════
// styles
// ════════════════════════════════════════════════════════════════════════════
const S = {
  screen: { minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f9fafb', fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  header: { height: 64, background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #fbbf24 100%)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px', flexShrink: 0, position: 'sticky', top: 0, zIndex: 100 },
  backBtn: { background: 'transparent', border: 'none', color: 'white', fontSize: 22, fontWeight: 700, cursor: 'pointer', width: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  headerTitle: { color: 'white', fontWeight: 700, fontSize: 16, flex: 1, textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', padding: '0 8px' },
  headerBtn: { background: 'rgba(255,255,255,0.22)', border: '1px solid rgba(255,255,255,0.5)', color: '#fff', borderRadius: 8, padding: '6px 10px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },
  body: { flex: 1, padding: '14px 14px 0', overflowY: 'auto' },
  centerBox: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24 },
  emptyBox: { textAlign: 'center', padding: '48px 16px' },
  filterRow: { display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 10, alignItems: 'center' },
  filterChip: { border: '1px solid', borderRadius: 16, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  refreshBtn: { marginLeft: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, width: 32, height: 32, fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  // Piece B — here now panel
  hereNowPanel: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '12px 14px', marginBottom: 14 },
  // Piece C — pending section header
  sectionBlock: { marginBottom: 14 },
  sectionHeader: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 },
  sectionDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  sectionTitle: { fontSize: 13, fontWeight: 800, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 },
  sectionCount: { fontSize: 11, fontWeight: 700, background: '#f97316', color: '#fff', borderRadius: 10, padding: '1px 7px', flexShrink: 0 },
  card: { background: '#fff', borderRadius: 14, padding: 14, marginBottom: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.07)' },
  avatar: { width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 },
  statusChip: { fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 14, whiteSpace: 'nowrap', flexShrink: 0 },
  stepBtn: { border: '1px solid', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  actionBtn: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer' },
  roundPop: { position: 'absolute', bottom: '110%', left: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 6px 18px rgba(0,0,0,0.14)', overflow: 'hidden', zIndex: 20, minWidth: 150 },
  roundOpt: { display: 'block', width: '100%', textAlign: 'left', background: '#fff', border: 'none', padding: '10px 12px', fontSize: 13, color: '#374151', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  modalSheet: { background: '#fff', width: '100%', maxWidth: 560, maxHeight: '92vh', borderTopLeftRadius: 18, borderTopRightRadius: 18, display: 'flex', flexDirection: 'column' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #f3f4f6', position: 'sticky', top: 0, background: '#fff', borderTopLeftRadius: 18, borderTopRightRadius: 18 },
  modalClose: { background: '#f3f4f6', border: 'none', borderRadius: 8, width: 32, height: 32, fontSize: 15, cursor: 'pointer' },
  modalActions: { display: 'flex', gap: 10, marginTop: 16 },
  fieldLabel: { fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4, display: 'block' },
  hint: { fontSize: 12, color: '#9ca3af', marginTop: 4 },
  input: { width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px', fontSize: 14, marginBottom: 4, fontFamily: 'inherit' },
  numInput: { width: 90, boxSizing: 'border-box', border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 9px', fontSize: 14 },
  // Stepper: -/+ flanking a number input (EV-508)
  stepperWrap: { display: 'inline-flex', alignItems: 'stretch', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff' },
  stepperBtn: { minWidth: 38, minHeight: 38, background: '#fff', border: '1px solid #e5e7eb', padding: '0 6px', fontSize: 18, fontWeight: 700, color: '#E46E34', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', userSelect: 'none', borderRadius: 0 },
  stepperInput: { width: 80, boxSizing: 'border-box', border: 'none', padding: '7px 6px', fontSize: 14, textAlign: 'center', fontFamily: 'inherit', outline: 'none', background: '#fff' },
  lineRow: { border: '1px solid #f3f4f6', borderRadius: 10, padding: 10, marginBottom: 8, background: '#fafafa' },
  removeBtn: { background: 'transparent', border: 'none', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  catChip: { background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', borderRadius: 16, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  errorBox: { marginTop: 12, padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 13 },
  primaryBtn: { flex: 1, background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 18px', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  secondaryBtn: { flex: 1, background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 12, padding: '12px 18px', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  chatScroll: { maxHeight: '50vh', minHeight: 180, overflowY: 'auto', background: '#fafafa', borderRadius: 12, padding: 12 },
  bubble: { maxWidth: '78%', padding: '8px 12px', borderRadius: 14, fontSize: 14, lineHeight: 1.4, wordBreak: 'break-word' },
  sysMsg: { textAlign: 'center', fontSize: 11, color: '#9ca3af', margin: '8px 0' },
};
